import { 
  doc as firestoreDoc, 
  getDoc,
  getDocs,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  increment,
  Timestamp
} from 'firebase/firestore';

import { db } from './firebase';
import { cacheManager } from './cacheManager';

/**
 * Classe para operações Firebase com cache
 */
class CachedFirebase {
  /**
   * Obtém um documento com suporte a cache
   * @param {string} path - Caminho completo do documento
   * @param {object} options - Opções de cache
   * @returns {Promise<object>} - Documento solicitado
   */
  async getDocument(path, options = {}) {
    const {
      forceRefresh = false,
      strategyType = 'dynamic',
      transformResponse = (data) => data
    } = options;
    
    // Verificar no cache primeiro, a menos que forceRefresh esteja ativado
    if (!forceRefresh) {
      const cachedData = cacheManager.get(path, {}, strategyType);
      
      if (cachedData && !cachedData.isStale) {
        // Cache válido, retornar imediatamente
        return transformResponse(cachedData.data);
      }
      
      if (cachedData && cachedData.isStale) {
        // Dados "stale", iniciar revalidação em background mas retornar o cache
        setTimeout(() => this._refreshDocument(path, strategyType, transformResponse), 0);
        return transformResponse(cachedData.data);
      }
    }
    
    // Cache miss ou forceRefresh, buscar dados
    return this._refreshDocument(path, strategyType, transformResponse);
  }
  
  /**
   * Método privado para atualizar um documento no cache
   * @private
   */
  async _refreshDocument(path, strategyType, transformResponse) {
    try {
      // Converter path para referência
      const docRef = firestoreDoc(db, path);
      const docSnap = await getDoc(docRef);
      
      let result = null;
      
      if (docSnap.exists()) {
        result = {
          id: docSnap.id,
          ...docSnap.data()
        };
        
        // Armazenar no cache
        cacheManager.set(path, {}, result, strategyType);
      }
      
      return transformResponse(result);
    } catch (error) {
      console.error(`Erro ao buscar documento ${path}:`, error);
      throw error;
    }
  }
  
  /**
   * Obtém uma coleção com suporte a cache
   * @param {string} path - Caminho da coleção
   * @param {object} queryOptions - Opções de consulta (where, orderBy, etc)
   * @param {object} cacheOptions - Opções de cache
   * @returns {Promise<Array>} - Lista de documentos
   */
  async getCollection(path, queryOptions = {}, cacheOptions = {}) {
    const {
      whereConditions = [],
      orderByFields = [],
      limitCount = null,
      startAfterDoc = null
    } = queryOptions;
    
    const {
      forceRefresh = false,
      strategyType = 'dynamic',
      transformResponse = (data) => data
    } = cacheOptions;
    
    // Gerar parâmetros de cache
    const cacheParams = {
      where: whereConditions,
      orderBy: orderByFields,
      limit: limitCount,
      startAfter: startAfterDoc ? startAfterDoc.id : null
    };
    
    // Verificar no cache primeiro, a menos que forceRefresh esteja ativado
    if (!forceRefresh) {
      const cachedData = cacheManager.get(path, cacheParams, strategyType);
      
      if (cachedData && !cachedData.isStale) {
        // Cache válido, retornar imediatamente
        return transformResponse(cachedData.data);
      }
      
      if (cachedData && cachedData.isStale) {
        // Dados "stale", iniciar revalidação em background mas retornar o cache
        setTimeout(() => this._refreshCollection(path, queryOptions, cacheParams, strategyType, transformResponse), 0);
        return transformResponse(cachedData.data);
      }
    }
    
    // Cache miss ou forceRefresh, buscar dados
    return this._refreshCollection(path, queryOptions, cacheParams, strategyType, transformResponse);
  }
  
  /**
   * Método privado para atualizar uma coleção no cache
   * @private
   */
  async _refreshCollection(path, queryOptions, cacheParams, strategyType, transformResponse) {
    try {
      const {
        whereConditions = [],
        orderByFields = [],
        limitCount = null,
        startAfterDoc = null
      } = queryOptions;
      
      // Construir a consulta
      const collectionRef = collection(db, path);
      const constraints = [];
      
      // Adicionar condições where
      whereConditions.forEach(condition => {
        constraints.push(where(condition.field, condition.operator, condition.value));
      });
      
      // Adicionar ordenação
      orderByFields.forEach(orderField => {
        constraints.push(orderBy(orderField.field, orderField.direction || 'asc'));
      });
      
      // Adicionar limite
      if (limitCount !== null) {
        constraints.push(limit(limitCount));
      }
      
      // Adicionar paginação
      if (startAfterDoc) {
        constraints.push(startAfter(startAfterDoc));
      }
      
      // Criar a consulta final
      const q = constraints.length > 0 
        ? query(collectionRef, ...constraints)
        : collectionRef;
      
      // Executar a consulta
      const querySnapshot = await getDocs(q);
      
      // Processar os resultados
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Armazenar no cache
      cacheManager.set(path, cacheParams, results, strategyType);
      
      // Retornar os resultados
      return transformResponse(results);
    } catch (error) {
      console.error(`Erro ao buscar coleção ${path}:`, error);
      throw error;
    }
  }
  
  /**
   * Invalidar o cache após operações de escrita
   * @param {string} path - Caminho do recurso
   * @param {boolean} invalidateCollection - Se deve invalidar a coleção pai
   */
  invalidateCache(path, invalidateCollection = true) {
    // Remover o documento específico do cache
    cacheManager.invalidate(path, {});
    
    // Se solicitado, invalidar a coleção pai
    if (invalidateCollection) {
      const lastSlashIndex = path.lastIndexOf('/');
      if (lastSlashIndex !== -1) {
        const collectionPath = path.substring(0, lastSlashIndex);
        cacheManager.invalidateByPathPrefix(collectionPath);
      }
    }
  }
  
  /**
   * Operações em lote com invalidação automática de cache
   * @param {Function} batchOperations - Função que recebe o batch e realiza operações
   * @param {Array<string>} pathsToInvalidate - Caminhos a serem invalidados após conclusão
   * @returns {Promise<void>}
   */
  async performBatchOperations(batchOperations, pathsToInvalidate = []) {
    try {
      // Criar batch
      const batch = writeBatch(db);
      
      // Executar operações
      await batchOperations(batch);
      
      // Commit do batch
      await batch.commit();
      
      // Invalidar caches após operações concluídas
      for (const path of pathsToInvalidate) {
        this.invalidateCache(path);
      }
    } catch (error) {
      console.error('Erro ao executar operações em lote:', error);
      throw error;
    }
  }
  
  /**
   * Obtém estatísticas de uso do cache
   * @returns {object} - Estatísticas do cache
   */
  getCacheStats() {
    return cacheManager.getStats();
  }
  
  /**
   * Limpa o cache
   */
  clearCache() {
    cacheManager.clear();
  }
}

// Exportar instância singleton
export const cachedFirebase = new CachedFirebase();

export default cachedFirebase; 